import React, { useEffect, useState } from 'react';
import { db } from '../firebase'; // adjust path as needed
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';

const AllClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterReservationType, setFilterReservationType] = useState('All'); // Options: All, single, group
  const [filterStudentAccepted, setFilterStudentAccepted] = useState('All'); // Options: All, Accepted, Not Accepted
  const [filterAuditionPaid, setFilterAuditionPaid] = useState('All'); // Options: All, Paid, Not Paid

  // Fetch all clients from Firestore
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'reservations'));
        const clientsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
      setLoading(false);
    };

    fetchClients();
  }, []);

  // Update the local state after updating Firestore
  const updateClientLocal = (clientId, newStatus) => {
    setClients(prevClients => 
      prevClients.map(client =>
        client.id === clientId
          ? { ...client, studentAccepted: newStatus }
          : client
      )
    );
  };

  // Handler for accepting a client
  const handleAccept = async (clientId) => {
    try {
      await updateDoc(doc(db, 'reservations', clientId), { studentAccepted: true });
      console.log(`Accepted client ${clientId}`);
      updateClientLocal(clientId, true);
    } catch (error) {
      console.error("Error updating client:", error);
    }
  };

  // Handler for denying a client
  const handleDeny = async (clientId) => {
    try {
      await updateDoc(doc(db, 'reservations', clientId), { studentAccepted: false });
      console.log(`Denied client ${clientId}`);
      updateClientLocal(clientId, false);
    } catch (error) {
      console.error("Error updating client:", error);
    }
  };

  // Apply filters
  const filteredClients = clients.filter(client => {
    const clientName = `${client.primaryContact?.firstName || ''} ${client.primaryContact?.lastName || ''}`.trim();
    const matchesSearch = clientName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesReservationType =
      filterReservationType === 'All' || client.reservationType === filterReservationType;

    const matchesStudentAccepted =
      filterStudentAccepted === 'All' ||
      (filterStudentAccepted === 'Accepted' && client.studentAccepted === true) ||
      (filterStudentAccepted === 'Not Accepted' && client.studentAccepted === false);

    const matchesAuditionPaid =
      filterAuditionPaid === 'All' ||
      (filterAuditionPaid === 'Paid' && client.auditionPaid === true) ||
      (filterAuditionPaid === 'Not Paid' && client.auditionPaid === false);

    return matchesSearch && matchesReservationType && matchesStudentAccepted && matchesAuditionPaid;
  });

  if (loading) {
    return <div>Loading clients...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-4">All Clients</h1>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="Search by Name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-2 border rounded bg-gray-50 text-gray-900"
        />

        <div className="flex flex-wrap gap-4">
          <select
            value={filterReservationType}
            onChange={(e) => setFilterReservationType(e.target.value)}
            className="p-2 border rounded bg-gray-50 text-gray-900"
          >
            <option value="All">All Reservation Types</option>
            <option value="single">Single</option>
            <option value="group">Group</option>
          </select>

          <select
            value={filterStudentAccepted}
            onChange={(e) => setFilterStudentAccepted(e.target.value)}
            className="p-2 border rounded bg-gray-50 text-gray-900"
          >
            <option value="All">All Statuses</option>
            <option value="Accepted">Accepted</option>
            <option value="Not Accepted">Not Accepted</option>
          </select>

          <select
            value={filterAuditionPaid}
            onChange={(e) => setFilterAuditionPaid(e.target.value)}
            className="p-2 border rounded bg-gray-50 text-gray-900"
          >
            <option value="All">All Payment Statuses</option>
            <option value="Paid">Paid</option>
            <option value="Not Paid">Not Paid</option>
          </select>
        </div>
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <p>No clients found.</p>
      ) : (
        <ul className="space-y-4">
        {filteredClients.map(client => (
          <li key={client.id} className="p-4 border rounded shadow">
            <p>
              <strong>Name:</strong>{' '}
              {`${client.primaryContact?.firstName || "N/A"} ${client.primaryContact?.lastName || ""}`}
            </p>
            <p>
              <strong>Email:</strong> {client.primaryContact?.email || "N/A"}
            </p>
            <p>
              <strong>Reservation Type:</strong> {client.reservationType}
            </p>
            <p>
              <strong>Student Accepted:</strong> {client.studentAccepted ? "True" : "False"}
            </p>
            <p>
              <strong>Audition Paid:</strong> {client.auditionPaid ? "True" : "False"}
            </p>
            <p>
              <strong>Instrument:</strong>{" "}
              {client.participants?.[0]?.instrument || "N/A"}
            </p>
            <div className="flex gap-4 mt-4">
              <button
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                onClick={() => handleAccept(client.id)}
              >
                Accept
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                onClick={() => handleDeny(client.id)}
              >
                Deny
              </button>
            </div>
          </li>
        ))}
      </ul>
      )}
    </div>
  );
};

export default AllClients;
